import * as React from "react";
import type { AnalysisParagraphResult } from "../../../agreement";
import type { CategoryId } from "../tokens";
import type { DetailTab, RowWithIndex, SelectedIssue } from "../types";
import { defaultTabForCategory } from "../utils";
import type { AiAnalysisViewModel } from "./useAiAnalysisViewModel";

export type CategoryExpansion = "preview" | "expanded";

export type UsePanelNavigationOptions = {
  vm: AiAnalysisViewModel;
  isEditing: boolean;
  onDiscardEditConfirm: () => boolean;
  onRevealIssueInDocument?: (row: AnalysisParagraphResult) => void | Promise<void>;
};

const DEFAULT_EXPANSION: Record<CategoryId, CategoryExpansion> = {
  needsAttention: "preview",
  needsReview: "preview",
  compliant: "preview",
};

export function usePanelNavigation({
  vm,
  isEditing,
  onDiscardEditConfirm,
  onRevealIssueInDocument,
}: UsePanelNavigationOptions) {
  const [categoryExpansion, setCategoryExpansion] =
    React.useState<Record<CategoryId, CategoryExpansion>>(DEFAULT_EXPANSION);
  const [selectedIssue, setSelectedIssue] = React.useState<SelectedIssue | null>(null);
  const [activeTab, setActiveTab] = React.useState<DetailTab>("analysis");
  const [focusedRowKey, setFocusedRowKey] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (vm.rows.length === 0) {
      setCategoryExpansion(DEFAULT_EXPANSION);
    }
  }, [vm.rows.length]);

  const openIssue = React.useCallback(
    (item: RowWithIndex, category: CategoryId) => {
      const categoryRows = vm.getRowsForCategory(category);
      const positionInCategory = categoryRows.findIndex((r) => r.key === item.key) + 1;
      setSelectedIssue({
        row: item.row,
        arrayIndex: item.arrayIndex,
        key: item.key,
        category,
        positionInCategory,
        totalInCategory: categoryRows.length,
      });
      setActiveTab(defaultTabForCategory(category));
      setFocusedRowKey(item.key);

      if (selectedIssue?.key !== item.key && onRevealIssueInDocument) {
        void Promise.resolve(onRevealIssueInDocument(item.row));
      }
    },
    [vm, selectedIssue?.key, onRevealIssueInDocument]
  );

  const closeDetail = React.useCallback(() => {
    if (isEditing && !onDiscardEditConfirm()) {
      return;
    }
    setSelectedIssue(null);
  }, [isEditing, onDiscardEditConfirm]);

  const expandCategory = React.useCallback((category: CategoryId) => {
    setCategoryExpansion((prev) => ({ ...prev, [category]: "expanded" }));
  }, []);

  const collapseCategory = React.useCallback((category: CategoryId) => {
    setCategoryExpansion((prev) => ({ ...prev, [category]: "preview" }));
  }, []);

  const toggleCategory = React.useCallback((category: CategoryId, count: number) => {
    if (count === 0) return;
    setCategoryExpansion((prev) => ({
      ...prev,
      [category]: prev[category] === "expanded" ? "preview" : "expanded",
    }));
  }, []);

  const syncSelectedIssue = React.useCallback(() => {
    setSelectedIssue((prev) => {
      if (!prev) return prev;
      const categoryRows = vm.getRowsForCategory(prev.category);
      const item = categoryRows.find((r) => r.row.index === prev.row.index);
      if (!item) return null;
      const positionInCategory = categoryRows.findIndex((r) => r.row.index === prev.row.index) + 1;
      return {
        ...prev,
        row: item.row,
        arrayIndex: item.arrayIndex,
        key: item.key,
        positionInCategory,
        totalInCategory: categoryRows.length,
      };
    });
  }, [vm]);

  const requestTabChange = React.useCallback(
    (tab: DetailTab) => {
      if (isEditing && !onDiscardEditConfirm()) {
        return;
      }
      setActiveTab(tab);
    },
    [isEditing, onDiscardEditConfirm]
  );

  const handleListKeyDown = React.useCallback(
    (e: React.KeyboardEvent, category: CategoryId) => {
      if (selectedIssue) return;
      const rows = vm.getRowsForCategory(category);
      if (rows.length === 0) return;

      const currentIdx = focusedRowKey
        ? rows.findIndex((r) => r.key === focusedRowKey)
        : -1;

      if (e.key === "j" || e.key === "J") {
        e.preventDefault();
        const next = Math.min(currentIdx + 1, rows.length - 1);
        setFocusedRowKey(rows[next >= 0 ? next : 0].key);
      } else if (e.key === "k" || e.key === "K") {
        e.preventDefault();
        const prev = Math.max(currentIdx - 1, 0);
        setFocusedRowKey(rows[prev].key);
      } else if (e.key === "Enter" && focusedRowKey) {
        e.preventDefault();
        const item = rows.find((r) => r.key === focusedRowKey);
        if (item) openIssue(item, category);
      }
    },
    [selectedIssue, vm, focusedRowKey, openIssue]
  );

  const handleDetailKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.tagName === "TEXTAREA" ||
        target.tagName === "INPUT" ||
        target.isContentEditable
      ) {
        return;
      }
      if (e.key === "Escape" || e.key === "Backspace") {
        e.preventDefault();
        closeDetail();
      }
    },
    [closeDetail]
  );

  return {
    categoryExpansion,
    selectedIssue,
    activeTab,
    focusedRowKey,
    openIssue,
    closeDetail,
    expandCategory,
    collapseCategory,
    toggleCategory,
    syncSelectedIssue,
    requestTabChange,
    setActiveTab,
    handleListKeyDown,
    handleDetailKeyDown,
  };
}
